import {
    Typography
} from '@ellucian/react-design-system/core';
import { withStyles } from '@ellucian/react-design-system/core/styles';
import {
    colorTextSecondary,
    spacingMedium,
    spacingSmall,
    spacingXSmall,
    spacingXxLarge
} from '@ellucian/react-design-system/core/styles/tokens';
import has from 'lodash/has';
import React = require('react');
import Line from './Line';
import { Condition } from './models/Group';
import helper from './models/Helper';
import { IQuery } from './models/Query';
import { IAttribute, ILookupValue, IRule, IQueryRuleComponentsProps } from './models/Rule';
import QueryRule, { IQueryRuleHeaderResources, IQueryRuleResources, QueryRuleHeader } from './QueryRule';

const styles = (/* theme */) => ({
    addRuleContainer: {
        marginBottom: '-60px',
        position: 'relative',
        whiteSpace: 'nowrap'
    },
    addRuleText: {
        color: colorTextSecondary,
        padding: '1rem',
        textAlign: 'left',
    },
    card: {
        '&:hover': {
            backgroundColor: '#F8F8F8',
        },
        'border': '1px solid blue',
        'cursor': 'pointer',
        'padding': '1rem',
        'textAlign': 'left',
    },
    container: {
        padding: spacingMedium,
        position: 'relative',
    },
    dividerContainer: {
        height: '40px',
        marginBottom: spacingXSmall,
        position: 'relative',
    },
    headerContainer: {
        marginBottom: spacingXSmall,
        position: 'relative',
    },
    newGroupButton: {
        bottom: '0px',
        left: '0px',
        paddingLeft: spacingSmall,
        paddingRight: spacingSmall,
        position: 'absolute',
    },
    readOnlyText: {
        color: colorTextSecondary,
        marginBottom: spacingXxLarge
    },
    ruleContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    source: {
        marginBottom: spacingMedium,
    },
});

interface IResources {
    addRule: string;
    newGroup: string;
    queryRule?: IQueryRuleResources;
    queryRuleHeader: IQueryRuleHeaderResources;
    readOnly: string;
    readOnlyNoCriteria: string;
}

export interface IQueryBuilder {
    onAlertError?: (message: string, showDefault?: boolean, key?: string) => void;
    attributeLookupLabel?: string;
    classes: any;
    debounceInterval?: number;
    fetchAttributeSuggestions?: (term: string, requestId: number) => Promise<any>;
    fetchLookupValueSuggestions?: (attribute: IAttribute, term: string, requestId: number) => Promise<ILookupValue>; // tslint:disable-line: max-line-length
    initialQuery?: IQuery;
    onAttributeChange: (attribute: IAttribute) => void;
    onQueryChange: (query: IQuery) => void;
    onEditingChange: (isEditing: boolean) => void;
    strings?: IResources;
    lookupModifiers?: object;
    readOnly?: boolean;
    selectAttributeMode?: boolean;
    queryRuleComponentsProps?: IQueryRuleComponentsProps;
}

interface IQueryBuilderState {
    editingRuleIds: string[];
    selectedGroupIds: string[];
    selectedRuleIds: string[];
    newRule: IRule;
    query: IQuery;
    version: number;
}

const noop = () => { /* no-op */ };
const supportedGroupDepth = 3;
const baseline = 'qbLineBaseline';
const idPrefix = 'queryBuilder';

export class QueryBuilderInternal extends React.Component<IQueryBuilder, IQueryBuilderState> {
    public static defaultProps = {
        fetchAttributeSuggestions: () => Promise.resolve([]),
        initialQuery: { groups: [], where: [] },
        onEditingChange: noop,
        onQueryChange: noop,
        readOnly: false,
        strings: {
            addRule: 'Add Rule',
            readOnly: 'Query criteria are locked in read-only mode.',
            // tslint:disable-next-line:max-line-length
            readOnlyNoCriteria: 'No criteria have been entered for this query, so all records of the target type will be returned.'
        }
    };
    /**
     * Initialize the query, prepping the groups and where rules to be renderable.
     */
    constructor(props: IQueryBuilder) {
        super(props);

        const version = helper.getVersion(props.initialQuery);
        const groups = helper.prepGroups(props.initialQuery.groups, props.initialQuery.where);
        const where = helper.prepRules(props.initialQuery.where, groups[0].id, groups);

        const query = { groups, where };

        this.state = {
            editingRuleIds: [],
            newRule: helper.emptyRule(groups[0].id),
            query,
            selectedGroupIds: [],
            selectedRuleIds: [],
            version,
        };

        helper.prepGroupButtons(query, supportedGroupDepth);
        helper.prepGroupLines(query);

        this.handleAddRule = this.handleAddRule.bind(this);
        this.handleDuplicateRule = this.handleDuplicateRule.bind(this);
        this.handleAddSelectedRules = this.handleAddSelectedRules.bind(this);
        this.handleDeleteRule = this.handleDeleteRule.bind(this);
        this.handleEditRuleCancel = this.handleEditRuleCancel.bind(this);
        this.handleEditRuleFinish = this.handleEditRuleFinish.bind(this);
        this.handleEditRuleStart = this.handleEditRuleStart.bind(this);
        this.handleSelectRule = this.handleSelectRule.bind(this);
        this.handleUnselectRule = this.handleUnselectRule.bind(this);
        this.handleUngroup = this.handleUngroup.bind(this);
        this.handleSelectGroup = this.handleSelectGroup.bind(this);
        this.handleUnselectGroup = this.handleUnselectGroup.bind(this);
        this.handleEditNewRuleCancel = this.handleEditNewRuleCancel.bind(this);
        this.handleEditNewRuleFinish = this.handleEditNewRuleFinish.bind(this);
        this.handleNewRuleChange = this.handleNewRuleChange.bind(this);
        this.handleAddGroupFromSelectedRules = this.handleAddGroupFromSelectedRules.bind(this);
        this.updateState = this.updateState.bind(this);
    }
    /**
     * Do a render of the query builder.
     */
    public render() {
        const {
            classes,
            debounceInterval,
            fetchAttributeSuggestions,
            fetchLookupValueSuggestions,
            onAttributeChange,
            strings,
            lookupModifiers,
            readOnly,
            selectAttributeMode,
            onAlertError,
            queryRuleComponentsProps,
        } = this.props;

        const {
            editingRuleIds,
            newRule,
            query,
            selectedGroupIds,
            selectedRuleIds,
        } = this.state;

        return (
            <div className={classes.container} data-line-id={baseline}>
                {/* Read-only explanation */}
                {readOnly &&
                    <Typography
                        id={`${idPrefix}_readOnly`}
                        variant="body2"
                        className={classes.readOnlyText}
                    >
                        {strings.readOnly}
                    </Typography>
                }

                {/* No criteria (read-only) warning */}
                {readOnly && query.where && query.where.length === 0 &&
                    <Typography
                        id={`${idPrefix}_noCriteriaWarning`}
                        variant="h4"
                    >
                        {strings.readOnlyNoCriteria}
                    </Typography>
                }

                {/* Rules Table Header */}
                {(!readOnly || !query.where || query.where.length > 0) &&
                    <div className={classes.headerContainer}>
                        <QueryRuleHeader
                            strings={strings.queryRuleHeader}
                            selectAttributeMode={selectAttributeMode ? selectAttributeMode : false}
                        />
                    </div>
                }

                {/* Rules Table */}
                <div className={classes.ruleContainer}>
                    {query.where.map((rule, index) => {
                        return (
                            <QueryRule
                                key={`${rule.$$ruleid}-ruleContainer`}
                                ruleIndex={index}
                                fetchAttributeSuggestions={fetchAttributeSuggestions}
                                fetchLookupValueSuggestions={fetchLookupValueSuggestions}
                                debounceInterval={debounceInterval}
                                disableAddGroupFromSelectedRules={!selectedRuleIds.length || selectedRuleIds.length < 2}
                                lineId={rule.$$ruleid}
                                supportedGroupDepth={supportedGroupDepth}
                                onAttributeChange={onAttributeChange}
                                onAddGroupFromSelectedRules={this.handleAddGroupFromSelectedRules}
                                onAddRule={this.handleAddRule}
                                onChangeGroupCondition={this.handleChangeGroupCondition}
                                onDuplicateRule={this.handleDuplicateRule}
                                onAddSelectedRules={this.handleAddSelectedRules}
                                onDelete={this.handleDeleteRule}
                                onEditCancel={this.handleEditRuleCancel}
                                onEditFinish={this.handleEditRuleFinish}
                                onEditStart={this.handleEditRuleStart}
                                onSelectRule={this.handleSelectRule}
                                onUnselectRule={this.handleUnselectRule}
                                onUngroup={this.handleUngroup}
                                onSelectGroup={this.handleSelectGroup}
                                onUnselectGroup={this.handleUnselectGroup}
                                selectedGroupIds={selectedGroupIds}
                                isSelected={selectedRuleIds.indexOf(rule.$$ruleid) >= 0}
                                isEditing={(!!selectAttributeMode || (!readOnly && editingRuleIds.indexOf(rule.$$ruleid) >= 0))}
                                rule={rule}
                                strings={strings.queryRule}
                                lookupModifiers={lookupModifiers}
                                readOnly={readOnly}
                                selectAttributeMode={selectAttributeMode ? selectAttributeMode : false}
                                onAlertError = {onAlertError}
                                queryRuleComponentsProps={queryRuleComponentsProps}
                            />
                        );
                    })}
                    {/* Divider under rules table */}
                    {query.where.length > 0 &&
                        <div className={classes.dividerContainer}/>
                    }
                </div>
                {/* New Rule */}
                {(!readOnly && (!selectAttributeMode || (selectAttributeMode && query.where.length === 0))) &&
                    <div>
                        <div className={classes.addRuleContainer}>
                            <Typography
                                className={classes.addRuleText}
                                id={`${idPrefix}_addRule`}
                            >
                                {strings.addRule}
                            </Typography>
                        </div>
                        <QueryRule
                            key={`${newRule.$$ruleid}-ruleContainer`}
                            ruleIndex="new"
                            debounceInterval={debounceInterval}
                            supportedGroupDepth={supportedGroupDepth}
                            fetchAttributeSuggestions={fetchAttributeSuggestions}
                            fetchLookupValueSuggestions={fetchLookupValueSuggestions}
                            onAttributeChange={onAttributeChange}
                            onEditCancel={this.handleEditNewRuleCancel}
                            onEditFinish={this.handleEditNewRuleFinish}
                            onRuleChanged={this.handleNewRuleChange}
                            isEditing={true}
                            rule={newRule}
                            lookupModifiers={lookupModifiers}
                            selectAttributeMode={selectAttributeMode ? selectAttributeMode : false}
                            onAlertError = {onAlertError}
                            strings={strings.queryRule}
                            queryRuleComponentsProps={queryRuleComponentsProps}
                        />
                    </div>
                }
                {/* Group Lines */}
                {query.groups.map((group) => {
                    const selected = this.state.selectedGroupIds.indexOf(group.id) >= 0;

                    return group.$$groupLines.map((groupLine) =>
                        <Line
                            selected={selected}
                            start={groupLine.start}
                            end={groupLine.end}
                            baseline={baseline}
                            orientation={groupLine.orientation}
                            key={groupLine.key}
                        />
                    );
                })}
            </div>
        );
    }
    /**
     * Add a sub-group for the selected rules.
     */
    public handleAddGroupFromSelectedRules = (groupId: string): void => {
        let { query } = this.state;
        const { selectedRuleIds } = this.state;

        const rules = query.where.filter((r) => selectedRuleIds.indexOf(r.$$ruleid) >= 0);

        query = helper.createGroupFromRules(rules, groupId, query, supportedGroupDepth);

        this.updateState({
            query,
            selectedRuleIds: [],
        });
    }
    /**
     * Add a new rule to the given group.
     */
    public handleAddRule = (groupId: string): void => {
        let { query } = this.state;
        const { editingRuleIds } = this.state;

        let rule: IRule;
        ({ query, rule } = helper.addRule(groupId, query));

        editingRuleIds.push(rule.$$ruleid);

        this.updateState({
            editingRuleIds,
            query,
        });
    }
    /**
     * Change the condition of the group to the given condition.
     */
    public handleChangeGroupCondition = (groupId: string, condition: Condition): void => {
        let { query } = this.state;

        query = helper.changeGroupCondition(groupId, condition, query);

        this.updateState({
            query,
        });
    }
    /**
     * Add a new duplicate rule of the given rule to the same group.
     */
    public handleDuplicateRule = (rule: IRule): void => {
        let { query } = this.state;
        const { editingRuleIds } = this.state;

        let duplicate: IRule;
        ({ query, rule: duplicate } = helper.addDuplicateRule(rule, query));

        editingRuleIds.push(duplicate.$$ruleid);

        this.updateState({
            editingRuleIds,
            query
        });
    }
    /**
     * Add the selected rules under the given group.
     */
    public handleAddSelectedRules = (groupId: string): void => {
        let { query } = this.state;
        const { selectedRuleIds } = this.state;

        const rules = query.where.filter((r) => selectedRuleIds.indexOf(r.$$ruleid) >= 0);
        query = helper.moveRulesToGroup(rules, groupId, query);

        this.updateState({
            query,
            selectedRuleIds: [],
        });
    }
    /**
     * Delete the given rule from the query.
     */
    public handleDeleteRule = (rule: IRule): void => {
        let { query, selectedRuleIds } = this.state;

        selectedRuleIds = selectedRuleIds.filter((id) => id !== rule.$$ruleid);

        query = helper.deleteRule(rule, query);

        this.updateState({
            query,
            selectedRuleIds
        });
    }
    /**
     * Handle the canceling of the edit of the given rule.  If it's a new rule then
     * delete the rule entirely.  If it's an existing rule that has already been
     * committed, then return the rule to it's state prior to editing.
     */
    public handleEditRuleCancel = (rule: IRule): void => {
        const { editingRuleIds, selectedRuleIds, query } = this.state;

        if (rule.$$isNew) {
            query.where = query.where.filter((r) => !helper.isSameRule(rule, r));

            this.updateState({
                editingRuleIds: editingRuleIds.filter((rid) => rule.$$ruleid !== rid),
                query,
                selectedRuleIds: selectedRuleIds.filter((rid) => rule.$$ruleid !== rid),
            });
        } else {
            this.updateState({
                editingRuleIds: editingRuleIds.filter((rid) => rule.$$ruleid !== rid)
            });
        }
    }
    /**
     * Handle when rule changes are commited by replacing rule in query.
     */
    public handleEditRuleFinish = (rule: IRule): void => {
        const { editingRuleIds, query } = this.state;

        rule.$$isNew = false;

        query.where = query.where.map((it) => {
            if (helper.isSameRule(it, rule)) {
                return Object.assign({}, it, rule);
            }
            return it;
        });

        this.updateState({
            editingRuleIds: editingRuleIds.filter((rid) => rule.$$ruleid !== rid),
            query
        });
    }
    /**
     * Handle when a rule editing begins.
     */
    public handleEditRuleStart = (rule: IRule): void => {
        const { editingRuleIds } = this.state;

        if (editingRuleIds.indexOf(rule.$$ruleid) < 0) {
            editingRuleIds.push(rule.$$ruleid);
        }

        this.updateState({editingRuleIds});
    }
    /**
     * Handle when a rule is selected.
     */
    public handleSelectRule = (rule: IRule): void => {
        const { selectedRuleIds } = this.state;

        if (selectedRuleIds.indexOf(rule.$$ruleid) < 0) {
            selectedRuleIds.push(rule.$$ruleid);
        }
        this.updateState({selectedRuleIds});
    }
    /**
     * Handle when a rule is unselected.
     */
    public handleUnselectRule = (rule: IRule): void => {
        const { selectedRuleIds } = this.state;

        this.updateState({
            selectedRuleIds: selectedRuleIds.filter((id) => id !== rule.$$ruleid),
        });
    }
    /**
     * Handle when a group is removed.
     */
    public handleUngroup = (groupId: string): void => {
        let { query } = this.state;

        query = helper.ungroup(groupId, query);

        this.updateState({
            query,
            selectedRuleIds: [],
        });
    }
    /**
     * Handle when a group is selected (hovered-over)
     */
    public handleSelectGroup = (groupId: string): void => {
        const { query, selectedGroupIds } = this.state;

        helper.getNestedGroupIds(groupId, query.where).forEach((gid) => {
            if (selectedGroupIds.indexOf(gid) < 0) {
                selectedGroupIds.push(gid);
            }
        });

        this.updateState({selectedGroupIds});
    }
    /**
     * Handle when a group is unselected (unhovered-over)
     */
    public handleUnselectGroup = (groupId: string): void => {
        const { query, selectedGroupIds } = this.state;

        const nestedGroups = helper.getNestedGroupIds(groupId, query.where);

        this.updateState({
            selectedGroupIds: selectedGroupIds.filter((gid) => nestedGroups.indexOf(gid) < 0)
        });
    }
    /**
     * Handle when the new rule is canceled (new rule should be cleared).
     */
    public handleEditNewRuleCancel = (): void => {
        const { query } = this.state;

        this.updateState({
            newRule: helper.emptyRule(query.groups[0].id),
        });
    }
    /**
     * Handle when the new rule is finished.
     */
    public handleEditNewRuleFinish = (rule: IRule): void => {
        const { query } = this.state;

        rule.$$isNew = false;
        query.where.push(rule);

        this.updateState({
            newRule: helper.emptyRule(query.groups[0].id),
            query
        });
    }
    /**
     * Handle when the new rule is changed.
     */
    public handleNewRuleChange = (rule: IRule): void => {
        const { newRule } = this.state;

        this.updateState({
            newRule: {...newRule, ...rule}
        });
    }
    /**
     * Use updateState for all state changes because 2nd-order actions occur based on what is
     * passed for partial state, such as notifying parent components or updating dependent
     * state attributes.
     */
    public updateState = (state: any) => {
        if (this.props.readOnly) {
            return;
        }

        const {
            query = this.state.query,
            selectedRuleIds = this.state.selectedRuleIds
        } = state;

        // refine rule lines whenever where changes
        const hasQueryChanged = has(state, 'query');
        if (hasQueryChanged) {
            helper.prepGroupButtons(query, supportedGroupDepth);
            helper.prepGroupLines(query);
        }

        // refine whether or not add selected rules is disabled whenever selection changes
        const hasSelectedRuleIdsChanged = has(state, 'selectedRuleIds');
        if (hasSelectedRuleIdsChanged) {
            helper.prepDisableAddSelectedRules(selectedRuleIds, query.where);
        }

        const hasEditingRuleIdsChanged = has(state, 'editingRuleIds');
        const hasNewRuleChanged = has(state, 'newRule');

        this.setState(state, () => {
            const {
                onEditingChange,
                onQueryChange,
            } = this.props;

            // let consumers know that query changed
            if (hasQueryChanged) {
                onQueryChange({
                    groups: helper.unprepGroups(this.state.query.groups),
                    version: this.state.version,
                    where: helper.unprepRules(this.state.query.where),
                });
            }

            // let consumers know that isEditing changed
            if (hasEditingRuleIdsChanged || hasNewRuleChanged) {
                // must redraw the lines if hasEditingRuleIdsChanged
                if (hasEditingRuleIdsChanged) {
                    window.dispatchEvent(new Event('customResize'));
                }

                const isEditing = !!this.state.editingRuleIds.length;
                const isEmptyNewRule = helper.isEmpty(this.state.newRule);
                onEditingChange(isEditing || !isEmptyNewRule);
            }
        });
    }
    /**
     * Adjusts the QueryBuilder UI if the initialQuery prop is changed after initial render.
     */
    public newInitialQuery = (props: IQueryBuilder) => {
        if (props && props.initialQuery) {
            const version = helper.getVersion(props.initialQuery);
            const groups = helper.prepGroups(props.initialQuery.groups, props.initialQuery.where);
            const where = helper.prepRules(props.initialQuery.where, groups[0].id, groups);

            const query = { groups, where };

            this.updateState({
                newRule: helper.emptyRule(groups[0].id),
                query,
                version
            });

            helper.prepGroupButtons(query, supportedGroupDepth);
            helper.prepGroupLines(query);
        }
    }
}

export default withStyles(styles)(QueryBuilderInternal);
